import { forwardRef } from "react";
import { DOC_TYPES, computeTotals, fileUrl, fmtDate, fmtMoney } from "@/lib/api";

export const DocumentPreview = forwardRef(function DocumentPreview({ doc, settings, shareToken, scale = 1 }, ref) {
  const meta = DOC_TYPES[doc.type] || DOC_TYPES.invoice;
  const t = computeTotals(doc);
  const s = settings || {};
  const accent = s.accent_color || "#D95D39";
  const sigId = doc.signature_mode === "manual" ? s.manual_signature_file_id : doc.signature_mode === "digital" ? doc.signature_file_id : "";
  const isPayment = doc.type === "receipt" || doc.type === "voucher";

  return (
    <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: 794 * scale, height: 1123 * scale }}>
      <div ref={ref} className="a4 p-14 flex flex-col" data-testid="document-preview">
        <div className="flex items-start justify-between gap-8 pb-8 border-b-2" style={{ borderColor: "#1a1a1a" }}>
          <div className="flex items-start gap-5">
            {s.logo_file_id && <img src={fileUrl(s.logo_file_id, shareToken)} alt="Logo" className="w-20 h-20 object-contain" crossOrigin="use-credentials" />}
            <div>
              <div className="font-display font-black text-2xl tracking-tight">{s.company_name || "Your Company"}</div>
              {s.tagline && <div className="text-sm text-neutral-600">{s.tagline}</div>}
              <div className="text-xs text-neutral-600 mt-2 whitespace-pre-line leading-relaxed">{[s.address, s.email, s.phone, s.website].filter(Boolean).join("\n")}</div>
              {s.tax_id && <div className="text-xs text-neutral-600 font-mono mt-1">Tax ID: {s.tax_id}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs tracking-[0.25em] uppercase font-semibold" style={{ color: accent }}>{meta.label}</div>
            <div className="font-mono text-2xl font-semibold mt-1 whitespace-nowrap">{doc.number || "DRAFT"}</div>
            {doc.title && <div className="text-sm text-neutral-600 mt-1">{doc.title}</div>}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-8 py-8 border-b border-neutral-200">
          <div className="col-span-1">
            <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-neutral-500 mb-2">{meta.partyLabel}</div>
            <div className="font-semibold">{doc.party?.name || "—"}</div>
            <div className="text-sm text-neutral-600 whitespace-pre-line leading-relaxed">{[doc.party?.address, doc.party?.email, doc.party?.phone].filter(Boolean).join("\n")}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-neutral-500 mb-2">{isPayment ? "Payment Date" : "Issue Date"}</div>
            <div className="font-mono text-sm">{fmtDate(doc.issue_date)}</div>
            {doc.due_date && !isPayment && (
              <>
                <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-neutral-500 mb-2 mt-4">{doc.type === "quotation" ? "Valid Until" : "Due Date"}</div>
                <div className="font-mono text-sm">{fmtDate(doc.due_date)}</div>
              </>
            )}
          </div>
          <div>
            {doc.payment_method && (
              <>
                <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-neutral-500 mb-2">Payment Method</div>
                <div className="text-sm">{doc.payment_method}</div>
              </>
            )}
            {doc.reference && (
              <>
                <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-neutral-500 mb-2 mt-4">Reference</div>
                <div className="font-mono text-sm">{doc.reference}</div>
              </>
            )}
            <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-neutral-500 mb-2 mt-4">Status</div>
            <div className="text-sm capitalize">{doc.status}</div>
          </div>
        </div>

        <table className="w-full border-collapse mt-8 text-sm">
          <thead>
            <tr className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">
              <th className="text-left py-3 font-semibold border-b border-neutral-300">Description</th>
              <th className="text-right py-3 font-semibold border-b border-neutral-300 w-20">Qty</th>
              <th className="text-right py-3 font-semibold border-b border-neutral-300 w-32">Unit Price</th>
              <th className="text-right py-3 font-semibold border-b border-neutral-300 w-32">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(doc.items || []).map((it, i) => (
              <tr key={i} className="border-b border-neutral-200 last:border-0">
                <td className="py-3 pr-4">{it.description || <span className="text-neutral-400">Untitled item</span>}</td>
                <td className="py-3 text-right font-mono">{Number(it.quantity || 0)}</td>
                <td className="py-3 text-right font-mono">{fmtMoney(it.unit_price, s)}</td>
                <td className="py-3 text-right font-mono">{fmtMoney(Number(it.quantity || 0) * Number(it.unit_price || 0), s)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="grid grid-cols-2 gap-12 mt-10">
          <div>
            {doc.notes && (
              <>
                <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-neutral-500 mb-2">Notes</div>
                <p className="text-sm text-neutral-700 whitespace-pre-line leading-relaxed">{doc.notes}</p>
              </>
            )}
            {s.bank_details && doc.type !== "voucher" && (
              <>
                <div className="text-[10px] tracking-[0.25em] uppercase font-semibold text-neutral-500 mb-2 mt-6">Payment Details</div>
                <p className="text-sm text-neutral-700 whitespace-pre-line font-mono leading-relaxed">{s.bank_details}</p>
              </>
            )}
          </div>
          <div className="font-mono text-sm">
            <div className="flex justify-between py-2 border-b border-neutral-200"><span className="text-neutral-500">Subtotal</span><span>{fmtMoney(t.subtotal, s)}</span></div>
            {Number(doc.discount) > 0 && <div className="flex justify-between py-2 border-b border-neutral-200"><span className="text-neutral-500">Discount</span><span>-{fmtMoney(doc.discount, s)}</span></div>}
            <div className="flex justify-between py-2 border-b border-neutral-200"><span className="text-neutral-500">{s.tax_label || "Tax"} ({Number(doc.tax_rate || 0)}%)</span><span>{fmtMoney(t.tax_amount, s)}</span></div>
            <div className="flex justify-between items-baseline py-4 mt-2 border-t-2" style={{ borderColor: "#1a1a1a" }}>
              <span className="text-[10px] tracking-[0.25em] uppercase font-semibold" style={{ color: accent }}>{meta.verb}</span>
              <span className="text-2xl font-semibold" data-testid="preview-total">{fmtMoney(t.total, s)}</span>
            </div>
            <div className="text-[10px] text-neutral-500 text-right">{s.currency_code}</div>
          </div>
        </div>

        <div className="mt-auto pt-10 flex items-end justify-between gap-8">
          <div className="text-xs text-neutral-500 max-w-xs leading-relaxed">{s.footer_note}</div>
          <div className="text-center min-w-[220px]">
            <div className="h-20 flex items-end justify-center">
              {sigId && <img src={fileUrl(sigId, shareToken)} alt="Signature" className="max-h-20 max-w-[220px] object-contain" crossOrigin="use-credentials" />}
            </div>
            <div className="border-t border-neutral-800 pt-2 text-xs">
              <div className="font-semibold">{doc.signer_name || s.company_name || "Authorised Signatory"}</div>
              <div className="text-neutral-500 text-[10px] tracking-[0.2em] uppercase">Authorised signature</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
